import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.css'],
})
export class MyAccountComponent implements OnInit, OnDestroy {
  user: any = {};
  badgeUrl: string | null = null;
  loading: boolean = false;
  requestProviderModal: boolean = false;
  availableProviders: any[] = [];
  allProviders: any[] = [];
  requestProviderLoading: boolean = false;
  requestProviderForm: FormGroup = new FormGroup({
    provider_ids: new FormControl([], Validators.required),
  });
  badgeUploadModal: boolean = false;
  badgeImg: any;
  badgeUploadForm: FormGroup = new FormGroup({
    badge_photo: new FormControl(null, Validators.required),
  });

  constructor(
    private data: DataserviceService,
    private message: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem('user') || '{}');
    const badgePath =
      this.user?.headshotUrl ||
      (this.user?.id ? `badge-${this.user.id}` : null);
    this.loadBadge(badgePath);
    this.loadAllProviders();
  }

  ngOnDestroy(): void {
    if (this.badgeUrl) {
      URL.revokeObjectURL(this.badgeUrl);
    }
  }

  get crmId(): string {
    return (
      this.user?.crm_id || this.user?.user_crm_id || this.user?.user_id || ''
    );
  }

  loadAllProviders() {
    this.data.getData('providers').subscribe({
      next: (res: any) => {
        this.allProviders = res?.data || [];
      },
      error: () => {
        this.allProviders = [];
      },
    });
  }

  get providerExternals(): Array<{ providerName: string; externalId: string }> {
    const externals =
      this.user?.provider_externals || this.user?.provider_external_ids || {};
    const userProviders = this.providerList;
    const providers =
      this.allProviders.length > 0 ? this.allProviders : userProviders;
    const result: Array<{ providerName: string; externalId: string }> = [];

    Object.entries(externals).forEach(([providerId, externalId]) => {
      const provider = providers.find((p: any) => {
        if (!p) return false;
        const pId = p?.id || p?.provider_id || p?.providerId;
        if (pId && String(pId) === String(providerId)) {
          return true;
        }

        return (
          pId && String(pId).toLowerCase() === String(providerId).toLowerCase()
        );
      });

      const providerName = provider?.name || 'Unknown Provider';

      if (externalId) {
        result.push({ providerName, externalId: String(externalId) });
      }
    });

    return result;
  }

  get fullName(): string {
    const first = this.user?.first_name || '';
    const last = this.user?.last_name || '';
    return `${first} ${last}`.trim();
  }

  get providerList(): any[] {
    if (Array.isArray(this.user?.providers)) {
      return this.user.providers;
    }
    return [];
  }

  private get companyId(): string | null {
    return (
      this.user?.company_id ||
      this.user?.companyId ||
      this.user?.company?.id ||
      null
    );
  }

  private get userId(): string | null {
    return this.user?.id || null;
  }

  async loadBadge(path?: string | null) {
    if (!path) {
      this.badgeUrl = null;
      return;
    }
    try {
      const badge = await this.data.getSupabaseBadge(path);
      
      if (this.badgeUrl) {
        URL.revokeObjectURL(this.badgeUrl);
      }
      this.badgeUrl = URL.createObjectURL(badge);
    } catch {
      this.badgeUrl = null;
    }
  }

  private extractProviderIds(list: any): string[] {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((p: any) => {
        if (!p) return null;
        if (typeof p === 'string' || typeof p === 'number') return String(p);
        return p.id || p.provider_id || p.providerId || null;
      })
      .filter((id: any): id is string => !!id);
  }

  openRequestProviderModal() {
    const companyId = this.companyId;
    const currentProviderIds = this.extractProviderIds(this.user?.providers);

    if (!companyId) {
      this.message.warning(
        'Company information is missing. Please contact an administrator.',
      );
      return;
    }

    this.requestProviderLoading = true;
    this.data
      .getData(`company/associated-company-info/${companyId}/${false}`)
      .subscribe({
        next: (res: any) => {
          this.requestProviderLoading = false;
          if (res?.result === 'success') {
            const companyProviders = res?.data?.providers || [];
            this.availableProviders = companyProviders.filter((p: any) => {
              const id = p?.id || p?.provider_id || p;
              return id && !currentProviderIds.includes(String(id));
            });

            if (!this.availableProviders.length) {
              this.message.info('No additional providers available to request');
              return;
            }

            this.requestProviderForm.reset({ provider_ids: [] });
            this.requestProviderModal = true;
          } else {
            this.message.error('Failed to load available providers');
          }
        },
        error: () => {
          this.requestProviderLoading = false;
          this.message.error('Failed to load available providers');
        },
      });
  }

  closeRequestProviderModal() {
    this.requestProviderModal = false;
    this.requestProviderForm.reset({ provider_ids: [] });
  }

  submitProviderRequest() {
    if (this.requestProviderForm.invalid) {
      this.requestProviderForm.markAllAsTouched();
      return;
    }

    const providerIds = this.requestProviderForm.get('provider_ids')?.value;
    const companyId = this.companyId;
    const userId = this.userId;

    if (
      !companyId ||
      !userId ||
      !Array.isArray(providerIds) ||
      providerIds.length === 0
    ) {
      this.message.error('Missing required information');
      return;
    }

    this.requestProviderLoading = true;
    this.data
      .postData(
        {
          user_id: userId,
          company_id: companyId,
          provider_ids: providerIds,
        },
        'users/request-provider',
      )
      .subscribe({
        next: (res: any) => {
          this.requestProviderLoading = false;
          if (res?.result === 'success') {
            this.message.success('Provider request submitted successfully');
            this.closeRequestProviderModal();
          } else {
            this.message.error(
              res?.message || 'Failed to submit provider request',
            );
          }
        },
        error: () => {
          this.requestProviderLoading = false;
          this.message.error('Failed to submit provider request');
        },
      });
  }

  openBadgeUpload() {
    this.badgeUploadModal = true;
  }

  closeBadgeUploadModal() {
    this.badgeUploadModal = false;
  }

  async submitBadgeUpload() {
    if (!this.badgeImg) {
      alert('Please choose a file first.');
      return;
    }

    const filePath = this.buildBadgePath();
    const res = await lastValueFrom(this.data.uploadBadgeImg(this.badgeImg, filePath));
    if (!res) {
      alert('Something went wrong uploading your photo');
      return;
    }

    const updatedUser = { ...this.user, headshotUrl: filePath };
    this.user = updatedUser;
    localStorage.setItem('user', JSON.stringify(updatedUser));

    await this.loadBadge(filePath);

    this.closeBadgeUploadModal();
  }

  private buildBadgePath(): string {
    const idPart = this.userId ? `badge-${this.userId}` : '';
    const first = (this.user?.first_name || '').toString();
    const last = (this.user?.last_name || '').toString();
    const namePart = `${first}-${last}-badge`;

    const raw = idPart || namePart || 'badge';
    return raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  onBadgeFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];
    this.badgeImg = file;
  }
}
